import { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  GoogleMap,
  LoadScript,
  Marker,
  OverlayView,
  DirectionsRenderer,
} from "@react-google-maps/api";
import {
  Button,
  Container,
  Stack,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { id } from "date-fns/locale";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { axiosPrivate } from "../../../common/axiosPrivate";

const mapContainerStyle = {
  width: "100%",
  height: "500px",
};

const defaultCenter = { lat: -6.149770617808631, lng: 106.18218026749454 };

const CustomLabel = ({ koordinat, label }) => (
  <OverlayView position={koordinat} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
    <div
      style={{
        position: "absolute",
        background: "#fff",
        padding: "4px 8px",
        borderRadius: "4px",
        boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.2)",
        whiteSpace: "nowrap",
        fontSize: "12px",
        fontWeight: "bold",
        transform: "translate(-110%, -50%)",
      }}
    >
      {label}
    </div>
  </OverlayView>
);

export default function TrackingPenagihanPage() {
  const [date, setDate] = useState(null);
  const [petugas, setPetugas] = useState("");
  const [petugasList, setPetugasList] = useState([]);
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [directionsList, setDirectionsList] = useState([]);
  const [totalDistance, setTotalDistance] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [legsInfo, setLegsInfo] = useState([]);
  const mapRef = useRef(null);

   useEffect(() => {
    const fetchPetugasList = async () => {
      try {
        const response = await axiosPrivate.get("/pegawai/dropdown/list/all", {
          params: {
            role: 'pusat'
          }
        });
        setPetugasList(response.data);
      } catch (error) {
        console.error("Error fetching petugas list:", error);
      }
    };
    fetchPetugasList();
  }, []);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    const roundedMins = remainder >= 30 ? mins + 1 : mins;
    return `${roundedMins} mins`;
  };

  const calculateRoutesInChunks = useCallback((data) => {
    if (data.length < 2) return;
    const maxWaypoints = 25;
    const chunks = [];
    let index = 0;

    while (index < data.length - 1) {
      const chunkOrigin = data[index].koordinat;
      const remaining = data.slice(index + 1);
      const chunkWaypoints = remaining.slice(0, maxWaypoints).map((item) => ({
        location: item.koordinat,
        stopover: true,
      }));
      const chunkDestination =
        remaining[Math.min(maxWaypoints, remaining.length - 1)]?.koordinat;
      if (!chunkDestination) break;

      chunks.push({
        origin: chunkOrigin,
        destination: chunkDestination,
        waypoints: chunkWaypoints,
        startIndex: index,
      });

      index += maxWaypoints;
    }

    const directionsService = new window.google.maps.DirectionsService();
    const promises = chunks.map(
      (chunk) =>
        new Promise((resolve) => {
          directionsService.route(
            {
              origin: chunk.origin,
              destination: chunk.destination,
              waypoints: chunk.waypoints,
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (status === window.google.maps.DirectionsStatus.OK) {
                resolve({ result, startIndex: chunk.startIndex });
              } else {
                console.error("Route chunk failed:", status);
                resolve(null);
              }
            }
          );
        })
    );

    Promise.all(promises).then((results) => {
      const validDirections = results.filter((r) => r !== null);
      setDirectionsList(validDirections.map((d) => d.result));

      let totalDist = 0;
      let totalDur = 0;
      const legsCombined = [];

      validDirections.forEach(({ result, startIndex }) => {
        result.routes[0].legs.forEach((leg, i) => {
          const dataIndex = startIndex + i;
          const trackingItem = data[dataIndex];
          const extraSeconds = trackingItem?.dtd ? 300 : 1200;
          totalDist += leg.distance.value;
          totalDur += leg.duration.value + extraSeconds;
          legsCombined.push({
            distance: leg.distance.text,
            duration: leg.duration.text,
            adjustedDuration: formatDuration(leg.duration.value + extraSeconds),
            time: data[dataIndex + 1]?.tanggal,
            startLocation: leg.start_location,
            endLocation: leg.end_location,
            steps: leg.steps,
          });
        });
      });

      setTotalDistance(totalDist);
      setTotalDuration(totalDur);
      setLegsInfo(legsCombined);
    });
  }, []);

  const fetchTrackingData = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const response = await axiosPrivate.post("/penagihan/get-by-petugas", {
        date: format(date, "yyyy-MM-dd"),
        petugas,
      });
      const sortedData = response.data.length
        ? [...response.data]
            .filter(
              (item) =>
                item.koordinat &&
                item.koordinat.lat != null &&
                item.koordinat.lng != null
            )
            .sort(
              (a, b) =>
                new Date(a.tanggal).getTime() - new Date(b.tanggal).getTime()
            )
        : [];

      setTrackingData(sortedData);
      setDirectionsList([]);
      setLegsInfo([]);
      if (sortedData.length >= 2) {
        calculateRoutesInChunks(sortedData);
      }
    } catch (error) {
      console.error("Error fetching tracking data:", error);
    } finally {
      setLoading(false);
    }
  }, [date, petugas, calculateRoutesInChunks]);

  const getTotalTertagih = (data) => {
    if (!Array.isArray(data)) return 0;
    return data.reduce((acc, item) => {
      const nominal = Number(item.nominal);
      return acc + (Number.isNaN(nominal) ? 0 : nominal);
    }, 0);
  };

  return (
    <>
      <Helmet>
        <title>Tracking Maps | SIP App</title>
      </Helmet>

      <Container>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Tracking Maps by Petugas
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: "center" }}>
          <LocalizationProvider adapterLocale={id} dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Tanggal"
              value={date}
              onChange={setDate}
              slotProps={{ textField: { size: "small", sx: { height: 40 } } }}
            />
          </LocalizationProvider>

          <Autocomplete
            options={petugasList}
            getOptionLabel={(option) => option.nama}
            value={petugasList.find((p) => p.id === petugas) || null}
            onChange={(_, newValue) => setPetugas(newValue ? newValue.id : "")}
            sx={{ width: 240, height: 40 }}
            renderInput={(params) => (
              <TextField {...params} label="Petugas" size="small" />
            )}
          />

          <Button
            variant="contained"
            onClick={fetchTrackingData}
            disabled={loading}
            sx={{ height: 40, minWidth: 100 }}
          >
            Submit
          </Button>
        </Stack>

        <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API} libraries={["geometry"]}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={12}
            center={
              trackingData.length ? trackingData[0].koordinat : defaultCenter
            }
            onLoad={(map) => {
              mapRef.current = map;
            }}
          >
            {trackingData.map((item, index) => (
              <div key={index}>
                <Marker
                  position={item.koordinat}
                  icon={{
                    url: "/assets/map/officer.png",
                    scaledSize: new window.google.maps.Size(40, 40),
                    anchor: new window.google.maps.Point(20, 40),
                  }}
                />
                <CustomLabel
                  koordinat={item.koordinat}
                  label={`P${index + 1} - ${
                    item.dtd
                      ? `${item.rumpun?.nama || "Unknown"} - ${item.anggota?.nama || "Unknown"}`
                      : item.rumpun?.nama || "Unknown"
                  } - ${formatInTimeZone(new Date(item.tanggal), "UTC", "HH:mm")}`}
                />
              </div>
            ))}

            {directionsList.map((dir, idx) => (
              <DirectionsRenderer key={idx} directions={dir} />
            ))}
          </GoogleMap>
        </LoadScript>
      </Container>

      {legsInfo.length > 0 && (
        <Container sx={{ mt: 2 }}>
          <Typography variant="h6">Route Distance Details</Typography>
          {trackingData.length > 0 && (
            <Typography variant="body1">
              Start at:{" "}
              {formatInTimeZone(new Date(trackingData[0].tanggal), "UTC", "HH:mm")}
            </Typography>
          )}
          <Typography variant="body1">
            Total Distance: {(totalDistance / 1000).toFixed(2)} km
          </Typography>
          <Typography variant="body1">
            Total Adjusted Duration: {formatDuration(totalDuration)}
          </Typography>
          <Typography variant="body1">
            Tertagih: {getTotalTertagih(trackingData).toLocaleString("id-ID")}
          </Typography>
          {legsInfo.map((leg, index) => {
            const startLabel = `P${index + 1}`;
            const endLabel = `P${index + 2}`;
            return (
              <Typography key={index} variant="body2">
                {`${startLabel} - ${endLabel}: ${leg.distance} | Scheduled: ${leg.duration} | Adjusted: ${leg.adjustedDuration} (Depart at: ${leg.time ? formatInTimeZone(new Date(leg.time), "UTC", "HH:mm") : "-"})`}
              </Typography>
            );
          })}
        </Container>
      )}
    </>
  );
}
